/**
 * The catalog of the tax profile's values: one entry per column a controller can see and edit on
 * the Tax Profile page, with the table and column it lives in, the wording the page shows for it,
 * the group it is listed under, and how it is typed.
 *
 * Both sides read this and nothing else describes a field twice. The api uses it as the allow-list
 * for the edit routes (a request names a field by `key`, never by table and column) and to parse
 * and check the typed value; the dashboard uses it for labels, groups, the editor a field gets and
 * how a stored value is formatted (digits are stored, `12-3456789` is shown).
 *
 * Values that live in their own list tables (owners, officers, subsidiaries, state tax ids) are not
 * here: they are TAX_PROFILE_LIST_COLUMNS at the foot of this file, addressed by key and row id.
 * Estimated payments have no entry in either list yet.
 * `tax_profiles.sales_tax_nexus_states` (a jsonb list) is left out until an editor for it exists.
 */

import {
  ACCOUNTING_METHODS,
  DE_FRANCHISE_TAX_METHODS,
  ENTITY_TYPE_VALUES,
  IRS_TAX_CLASSIFICATIONS,
  K1_PARTNER_ENTITY_TYPE_LABELS,
  K1_PARTNER_ENTITY_TYPE_VALUES,
  SPECIAL_RETURN_TYPE_VALUES,
  STATE_TAX_TYPE_LABELS,
  STATE_TAX_TYPE_VALUES,
  TAX_YEAR_TYPES,
} from './taxProfile';
import type { TaxProfileListName } from '../types/taxProfile';

export type TaxProfileFieldTable =
  | 'legal_entities'
  | 'tax_profiles'
  | 'tax_profile_yearly_values';

export type TaxProfileFieldGroup =
  | 'identity'
  | 'address'
  | 'method'
  | 'filing'
  | 'delaware'
  | 'shares'
  | 'yearly'
  | 'payroll';

/** The groups in the order the page lists them; the section index jumps to these. */
export const TAX_PROFILE_FIELD_GROUPS: ReadonlyArray<{
  key: TaxProfileFieldGroup;
  label: string;
}> = [
  { key: 'identity', label: 'Legal identity' },
  { key: 'address', label: 'Address and contact' },
  { key: 'method', label: 'Tax year and accounting method' },
  { key: 'filing', label: 'Filing and registrations' },
  { key: 'delaware', label: 'Delaware annual tax' },
  { key: 'shares', label: 'Shares and ownership' },
  { key: 'yearly', label: 'Yearly values' },
  { key: 'payroll', label: 'Workforce and payroll' },
];

/**
 * How a value is typed, which decides the editor the page shows and the format a stored value is
 * displayed in. `number` and `money` columns read back from Postgres as strings; the page formats
 * them and the api parses them. `month` is 1 to 12. `month_day` is a month and a day with no year,
 * stored as MM-DD ('01-01') and shown as 'January 1'. `state` is a two-letter code. `ein` and `phone`
 * are stored as bare digits. `tin` is an owner's or an officer's taxpayer number (an SSN or an EIN):
 * stored encrypted, sent to the page only masked to its last four digits, and typed as nine digits.
 */
export type TaxProfileFieldType =
  | 'text'
  | 'longtext'
  | 'number'
  | 'money'
  | 'percent'
  | 'boolean'
  | 'date'
  | 'month'
  | 'month_day'
  | 'year'
  | 'choice'
  | 'state'
  | 'ein'
  | 'phone'
  | 'email'
  | 'naics'
  | 'tin';

/**
 * The shape check the api runs on an edited value, named as the tags validateAnswerValue in
 * api/src/services/taxProfileExtraction/validators.ts understands (the same checks the pipeline
 * runs on the model's answers). A `choice` field is checked against its `choices` instead.
 */
export type TaxProfileValidationTag =
  | 'ein'
  | 'tin'
  | 'state'
  | 'zip'
  | 'phone'
  | 'date'
  | 'month_day'
  | 'year'
  | 'month'
  | 'naics'
  | 'percent'
  | 'count'
  | 'money'
  | 'amount'
  | 'boolean';

export type TaxProfileFieldChoice = { value: string; label: string };

/**
 * The words the page shows for the true and the false value of a `boolean` field, in place of Yes
 * and No, and the options its select offers. For a column whose Yes or No would not say what was
 * meant, such as k1_partners.foreign_person under the heading "US person". What is stored stays a
 * real true or false; the api does not read these.
 */
export type TaxProfileBooleanLabels = { true: string; false: string };

/** What the page says for k1_partners.foreign_person: false is a US person, true is not. */
export const FOREIGN_PERSON_LABELS: TaxProfileBooleanLabels = {
  true: 'Not a US person',
  false: 'US person',
};

/**
 * What every entry of TAX_PROFILE_FIELDS must have. TAX_PROFILE_FIELDS is checked against this, and
 * the rest of the code uses TaxProfileFieldDefinition below, which is the same with `key` narrowed
 * to the keys the list actually has.
 */
type TaxProfileFieldShape = {
  /** The name the api and the page address the field by. */
  key: string;
  table: TaxProfileFieldTable;
  column: string;
  /** The wording on the page (a controller's words, never a column name). */
  label: string;
  group: TaxProfileFieldGroup;
  type: TaxProfileFieldType;
  /** The stored values and their wording, for a `choice` field. */
  choices?: ReadonlyArray<TaxProfileFieldChoice>;
  /** The words for true and false of a `boolean` field, where Yes and No would not say it. */
  booleanLabels?: TaxProfileBooleanLabels;
  /** True for a column of tax_profile_yearly_values: one value per tax year. */
  yearly?: true;
  validate?: TaxProfileValidationTag;
};

const choices = (
  values: ReadonlyArray<string>,
  labels: Readonly<Record<string, string>>
): TaxProfileFieldChoice[] =>
  values.map((value) => ({ value, label: labels[value] ?? value }));

/** Turns the stored token into the words the page shows, and supplies the options of the entity type dropdown. */
export const ENTITY_TYPE_LABELS: Readonly<Record<string, string>> = {
  llc: 'Limited liability company',
  corporation: 'Corporation',
  partnership: 'Partnership',
  sole_proprietorship: 'Sole proprietorship',
  nonprofit: 'Nonprofit',
  other: 'Other',
};

/** Turns the stored token into the words the page shows, and supplies the options of the tax classification dropdown. */
export const IRS_TAX_CLASSIFICATION_LABELS: Readonly<Record<string, string>> = {
  partnership: 'Partnership',
  c_corporation: 'C corporation',
  s_corporation: 'S corporation',
  disregarded_entity: 'Disregarded entity',
  nonprofit: 'Nonprofit',
};

/** Turns the stored token into the words the page shows, and supplies the options of the special return type dropdown. */
export const SPECIAL_RETURN_TYPE_LABELS: Readonly<Record<string, string>> = {
  initial: 'Initial return',
  final: 'Final return',
  name_change: 'Name change',
  address_change: 'Address change',
  other: 'Other',
};

/** Turns the stored token into the words the page shows, and supplies the options of the tax year type dropdown. */
export const TAX_YEAR_TYPE_LABELS: Readonly<Record<string, string>> = {
  calendar: 'Calendar year',
  fiscal: 'Fiscal year',
};

/** Turns the stored token into the words the page shows, and supplies the options of the accounting method dropdown. */
export const ACCOUNTING_METHOD_LABELS: Readonly<Record<string, string>> = {
  cash: 'Cash',
  accrual: 'Accrual',
};

/** Turns the stored token into the words the page shows, and supplies the options of the Delaware annual tax method dropdown. */
export const DE_FRANCHISE_TAX_METHOD_LABELS: Readonly<Record<string, string>> =
  {
    flat_tax: 'Flat tax',
    authorized_shares: 'Authorized shares method',
    assumed_par_value: 'Assumed par value method',
  };

/**
 * Hands a list back unchanged. It exists for the type: every entry is checked against the shape D,
 * and TypeScript infers K as the union of the keys actually written in the list, so the definition
 * and key types below name only those keys. A plain `ReadonlyArray<D>` annotation would widen every
 * key to string. Two calls, catalogOf<D>()(list), because TypeScript cannot take D explicitly and
 * infer K in the same call. `satisfies` alone would check the entries but still type each key as
 * string; `as const satisfies` would keep the keys but needs TypeScript 5.0 (the frontend is on
 * 4.7) and makes every nested array readonly.
 */
const catalogOf =
  <D extends { key: string }>() =>
  <K extends string>(entries: ReadonlyArray<D & { key: K }>) =>
    entries;

export const TAX_PROFILE_FIELDS = catalogOf<TaxProfileFieldShape>()([
  // Legal identity
  {
    key: 'legal_name',
    table: 'legal_entities',
    column: 'entity_name',
    label: 'Legal name',
    group: 'identity',
    type: 'text',
  },
  {
    key: 'dba_name',
    table: 'tax_profiles',
    column: 'dba_name',
    label: 'Doing business as',
    group: 'identity',
    type: 'text',
  },
  {
    key: 'federal_ein',
    table: 'tax_profiles',
    column: 'federal_ein',
    label: 'Federal EIN',
    group: 'identity',
    type: 'ein',
    validate: 'ein',
  },
  {
    key: 'entity_type',
    table: 'tax_profiles',
    column: 'entity_type',
    label: 'Entity type',
    group: 'identity',
    type: 'choice',
    choices: choices(ENTITY_TYPE_VALUES, ENTITY_TYPE_LABELS),
  },
  {
    key: 'irs_tax_classification',
    table: 'tax_profiles',
    column: 'irs_tax_classification',
    label: 'Tax classification',
    group: 'identity',
    type: 'choice',
    choices: choices(IRS_TAX_CLASSIFICATIONS, IRS_TAX_CLASSIFICATION_LABELS),
  },
  {
    key: 'state_of_incorporation',
    table: 'tax_profiles',
    column: 'state_of_incorporation',
    label: 'State of incorporation',
    group: 'identity',
    type: 'state',
    validate: 'state',
  },
  {
    key: 'incorporation_date',
    table: 'tax_profiles',
    column: 'incorporation_date',
    label: 'Incorporation date',
    group: 'identity',
    type: 'date',
    validate: 'date',
  },
  {
    key: 'de_entity_file_number',
    table: 'tax_profiles',
    column: 'de_entity_file_number',
    label: 'Delaware file number',
    group: 'identity',
    type: 'text',
  },
  {
    key: 'primary_industry_label',
    table: 'tax_profiles',
    column: 'primary_industry_label',
    label: 'Industry',
    group: 'identity',
    type: 'text',
  },
  {
    key: 'naics_code',
    table: 'tax_profiles',
    column: 'naics_code',
    label: 'NAICS code',
    group: 'identity',
    type: 'naics',
    validate: 'naics',
  },
  {
    key: 'business_activity_description',
    table: 'tax_profiles',
    column: 'business_activity_description',
    label: 'Business activity',
    group: 'identity',
    type: 'longtext',
  },

  // Address and contact
  {
    key: 'street_address',
    table: 'tax_profiles',
    column: 'street_address',
    label: 'Street address',
    group: 'address',
    type: 'text',
  },
  {
    key: 'address_line_2',
    table: 'tax_profiles',
    column: 'address_line_2',
    label: 'Address line 2',
    group: 'address',
    type: 'text',
  },
  {
    key: 'city',
    table: 'tax_profiles',
    column: 'city',
    label: 'City',
    group: 'address',
    type: 'text',
  },
  {
    key: 'state',
    table: 'tax_profiles',
    column: 'state',
    label: 'State',
    group: 'address',
    type: 'state',
    validate: 'state',
  },
  {
    key: 'zip_code',
    table: 'tax_profiles',
    column: 'zip_code',
    label: 'ZIP code',
    group: 'address',
    type: 'text',
    validate: 'zip',
  },
  {
    key: 'country',
    table: 'tax_profiles',
    column: 'country',
    label: 'Country',
    group: 'address',
    type: 'text',
  },
  {
    key: 'company_phone',
    table: 'tax_profiles',
    column: 'company_phone',
    label: 'Company phone',
    group: 'address',
    type: 'phone',
    validate: 'phone',
  },
  {
    key: 'finance_contact_email',
    table: 'tax_profiles',
    column: 'finance_contact_email',
    label: 'Finance contact',
    group: 'address',
    type: 'email',
  },

  // Tax year and accounting method
  {
    key: 'tax_year_type',
    table: 'tax_profiles',
    column: 'tax_year_type',
    label: 'Tax year type',
    group: 'method',
    type: 'choice',
    choices: choices(TAX_YEAR_TYPES, TAX_YEAR_TYPE_LABELS),
  },
  {
    key: 'fiscal_year_end_month',
    table: 'tax_profiles',
    column: 'fiscal_year_end_month',
    label: 'Fiscal year end',
    group: 'method',
    type: 'month',
    validate: 'month',
  },
  {
    key: 'tax_year_start',
    table: 'tax_profiles',
    column: 'tax_year_start',
    label: 'Tax year start',
    group: 'method',
    type: 'month_day',
    validate: 'month_day',
  },
  {
    key: 'accounting_method',
    table: 'tax_profiles',
    column: 'accounting_method',
    label: 'Accounting method',
    group: 'method',
    type: 'choice',
    choices: choices(ACCOUNTING_METHODS, ACCOUNTING_METHOD_LABELS),
  },
  {
    key: 's_corp_election_date',
    table: 'tax_profiles',
    column: 's_corp_election_date',
    label: 'S corporation election date',
    group: 'method',
    type: 'date',
    validate: 'date',
  },
  {
    key: 'prior_year_tax_provider',
    table: 'tax_profiles',
    column: 'prior_year_tax_provider',
    label: 'Prior year preparer',
    group: 'method',
    type: 'text',
  },

  // Filing and registrations
  {
    key: 'prior_year_return_on_file',
    table: 'tax_profiles',
    column: 'prior_year_return_on_file',
    label: 'Prior year return on file',
    group: 'filing',
    type: 'boolean',
    validate: 'boolean',
  },
  {
    key: 'filing_frequency',
    table: 'tax_profiles',
    column: 'filing_frequency',
    label: 'Filing frequency',
    group: 'filing',
    type: 'text',
  },
  {
    key: 'sales_tax_registered',
    table: 'tax_profiles',
    column: 'sales_tax_registered',
    label: 'Registered for sales tax',
    group: 'filing',
    type: 'boolean',
    validate: 'boolean',
  },

  // Delaware annual tax
  {
    key: 'de_franchise_tax_obligation',
    table: 'tax_profiles',
    column: 'de_franchise_tax_obligation',
    label: 'Owes Delaware annual tax',
    group: 'delaware',
    type: 'boolean',
    validate: 'boolean',
  },
  {
    key: 'de_franchise_tax_method',
    table: 'tax_profiles',
    column: 'de_franchise_tax_method',
    label: 'How it is calculated',
    group: 'delaware',
    type: 'choice',
    choices: choices(DE_FRANCHISE_TAX_METHODS, DE_FRANCHISE_TAX_METHOD_LABELS),
  },

  // Shares and ownership
  {
    key: 'authorized_shares_common',
    table: 'tax_profiles',
    column: 'authorized_shares_common',
    label: 'Authorized common shares',
    group: 'shares',
    type: 'number',
    validate: 'count',
  },
  {
    key: 'authorized_shares_preferred',
    table: 'tax_profiles',
    column: 'authorized_shares_preferred',
    label: 'Authorized preferred shares',
    group: 'shares',
    type: 'number',
    validate: 'count',
  },
  {
    key: 'par_value_common',
    table: 'tax_profiles',
    column: 'par_value_common',
    label: 'Par value, common',
    group: 'shares',
    type: 'money',
    validate: 'amount',
  },
  {
    key: 'par_value_preferred',
    table: 'tax_profiles',
    column: 'par_value_preferred',
    label: 'Par value, preferred',
    group: 'shares',
    type: 'money',
    validate: 'amount',
  },
  {
    key: 'shares_outstanding_common',
    table: 'tax_profiles',
    column: 'shares_outstanding_common',
    label: 'Common shares outstanding',
    group: 'shares',
    type: 'number',
    validate: 'count',
  },
  {
    key: 'shares_outstanding_preferred',
    table: 'tax_profiles',
    column: 'shares_outstanding_preferred',
    label: 'Preferred shares outstanding',
    group: 'shares',
    type: 'number',
    validate: 'count',
  },
  {
    key: 'ownership_structure',
    table: 'tax_profiles',
    column: 'ownership_structure',
    label: 'Ownership structure',
    group: 'shares',
    type: 'longtext',
  },
  {
    key: 'parent_company_name',
    table: 'tax_profiles',
    column: 'parent_company_name',
    label: 'Parent company',
    group: 'shares',
    type: 'text',
  },
  {
    key: 'parent_company_ein',
    table: 'tax_profiles',
    column: 'parent_company_ein',
    label: 'Parent company EIN',
    group: 'shares',
    type: 'ein',
    validate: 'ein',
  },
  {
    key: 'controlled_group_member',
    table: 'tax_profiles',
    column: 'controlled_group_member',
    label: 'Member of a controlled group',
    group: 'shares',
    type: 'boolean',
    validate: 'boolean',
  },
  {
    key: 'publicly_traded',
    table: 'tax_profiles',
    column: 'publicly_traded',
    label: 'Publicly traded',
    group: 'shares',
    type: 'boolean',
    validate: 'boolean',
  },

  // Yearly values (one row per tax year)
  {
    key: 'total_assets',
    table: 'tax_profile_yearly_values',
    column: 'total_assets',
    label: 'Total assets',
    group: 'yearly',
    type: 'money',
    yearly: true,
    validate: 'money',
  },
  {
    key: 'gross_assets',
    table: 'tax_profile_yearly_values',
    column: 'gross_assets',
    label: 'Gross assets',
    group: 'yearly',
    type: 'money',
    yearly: true,
    validate: 'money',
  },
  {
    key: 'num_shareholders_members',
    table: 'tax_profile_yearly_values',
    column: 'num_shareholders_members',
    label: 'Members at year end',
    group: 'yearly',
    type: 'number',
    yearly: true,
    validate: 'count',
  },
  {
    key: 'special_return_type',
    table: 'tax_profile_yearly_values',
    column: 'special_return_type',
    label: 'Special return type',
    group: 'yearly',
    type: 'choice',
    yearly: true,
    choices: choices(SPECIAL_RETURN_TYPE_VALUES, SPECIAL_RETURN_TYPE_LABELS),
  },
  {
    key: 'amended_return',
    table: 'tax_profile_yearly_values',
    column: 'amended_return',
    label: 'Amended return',
    group: 'yearly',
    type: 'boolean',
    yearly: true,
    validate: 'boolean',
  },
  {
    key: 'officer_compensation_total',
    table: 'tax_profile_yearly_values',
    column: 'officer_compensation_total',
    label: 'Officer compensation',
    group: 'yearly',
    type: 'money',
    yearly: true,
    validate: 'money',
  },

  // Workforce and payroll
  {
    key: 'payroll_provider',
    table: 'tax_profiles',
    column: 'payroll_provider',
    label: 'Payroll provider',
    group: 'payroll',
    type: 'text',
  },
  {
    key: 'actively_running_payroll',
    table: 'tax_profiles',
    column: 'actively_running_payroll',
    label: 'Running payroll',
    group: 'payroll',
    type: 'boolean',
    validate: 'boolean',
  },
  {
    key: 'num_w2_employees',
    table: 'tax_profile_yearly_values',
    column: 'num_w2_employees',
    label: 'Number of W-2 employees',
    group: 'payroll',
    type: 'number',
    yearly: true,
    validate: 'count',
  },
  {
    key: 'num_1099_contractors',
    table: 'tax_profile_yearly_values',
    column: 'num_1099_contractors',
    label: 'Number of 1099 contractors',
    group: 'payroll',
    type: 'number',
    yearly: true,
    validate: 'count',
  },
  {
    key: 'total_w2_wages',
    table: 'tax_profile_yearly_values',
    column: 'total_w2_wages',
    label: 'Total W-2 wages',
    group: 'payroll',
    type: 'money',
    yearly: true,
    validate: 'money',
  },
]);

/** One entry of TAX_PROFILE_FIELDS, its key one of the keys the list has. */
export type TaxProfileFieldDefinition = (typeof TAX_PROFILE_FIELDS)[number];
export type TaxProfileFieldKey = TaxProfileFieldDefinition['key'];

/** The catalog entry for a key, or undefined for a key the catalog does not have. */
export const taxProfileFieldByKey = (
  key: string
): TaxProfileFieldDefinition | undefined =>
  TAX_PROFILE_FIELDS.find((field) => field.key === key);

// ─── The columns of the list tables ──────────────────────────────────────────

export type TaxProfileListTable =
  | 'k1_partners'
  | 'k1_partners_tax_years'
  | 'entity_officers'
  | 'entity_subsidiaries'
  | 'entity_state_tax_ids';

/**
 * What every entry of TAX_PROFILE_LIST_COLUMNS must have; the rest of the code uses
 * TaxProfileListColumnDefinition below, the same with `key` narrowed to the keys the list has.
 */
type TaxProfileListColumnShape = {
  /** The name the api and the page address the column by; never one of TAX_PROFILE_FIELDS' keys. */
  key: string;
  table: TaxProfileListTable;
  column: string;
  label: string;
  type: TaxProfileFieldType;
  choices?: ReadonlyArray<TaxProfileFieldChoice>;
  /** The words for true and false of a `boolean` column, where Yes and No would not say it. */
  booleanLabels?: TaxProfileBooleanLabels;
  validate?: TaxProfileValidationTag;
  /** True for a column of k1_partners_tax_years: one value per owner per tax year. */
  yearly?: true;
  /** The column is NOT NULL, so a write may not clear it. */
  required?: true;
  /** The column on the same row that holds the tax year the value is for, written with it. */
  taxYearColumn?: string;
  /**
   * True for a column holding a taxpayer number as an AES-256-GCM payload (k1_partners.tin_encrypted,
   * entity_officers.ssn_encrypted). The api sends its values only masked to their last four digits,
   * compares them by their decrypted digits, and writes `hashColumn` beside the column.
   */
  encrypted?: true;
  /** The column on the same row holding the HMAC of the taxpayer number's digits. */
  hashColumn?: 'tin_hash' | 'ssn_hash';
};

/**
 * The columns of the owner, officer, subsidiary and state registration tables a person reviews and
 * types on the Tax Profile page: the columns promotion records candidates for (EXTRACTION_FIELD_MAP
 * in api/src/taxProfile/promotion/extractionFieldMap.ts). The two taxpayer numbers among them
 * (owner_tin, officer_ssn) are marked `encrypted`: the page only ever receives them masked.
 *
 * A separate list from TAX_PROFILE_FIELDS so that the page's groups of single values do not list
 * them and taxProfileFieldByKey does not return them. A write names one of these by `key` together
 * with `recordId`, the id of the row: the owner, officer, subsidiary or state registration; for a
 * k1_partners_tax_years column, the owner, with the tax year.
 */
export const TAX_PROFILE_LIST_COLUMNS = catalogOf<TaxProfileListColumnShape>()([
  // k1_partners: who the owner is
  {
    key: 'owner_full_legal_name',
    table: 'k1_partners',
    column: 'full_legal_name',
    label: 'Name',
    type: 'text',
    required: true,
  },
  {
    key: 'owner_tin',
    table: 'k1_partners',
    column: 'tin_encrypted',
    label: 'Taxpayer no.',
    type: 'tin',
    validate: 'tin',
    encrypted: true,
    hashColumn: 'tin_hash',
  },
  {
    key: 'owner_entity_type',
    table: 'k1_partners',
    column: 'entity_type',
    label: 'Type',
    type: 'choice',
    choices: choices(
      K1_PARTNER_ENTITY_TYPE_VALUES,
      K1_PARTNER_ENTITY_TYPE_LABELS
    ),
  },
  {
    key: 'owner_foreign_person',
    table: 'k1_partners',
    column: 'foreign_person',
    label: 'US person',
    type: 'boolean',
    booleanLabels: FOREIGN_PERSON_LABELS,
    validate: 'boolean',
  },
  {
    key: 'owner_street_address',
    table: 'k1_partners',
    column: 'street_address',
    label: 'Street address',
    type: 'text',
  },
  {
    key: 'owner_city',
    table: 'k1_partners',
    column: 'city',
    label: 'City',
    type: 'text',
  },
  {
    key: 'owner_state',
    table: 'k1_partners',
    column: 'state',
    label: 'State',
    type: 'state',
    validate: 'state',
  },
  {
    key: 'owner_zip_code',
    table: 'k1_partners',
    column: 'zip_code',
    label: 'ZIP code',
    type: 'text',
    validate: 'zip',
  },

  // k1_partners_tax_years: the owner's numbers for one tax year
  {
    key: 'owner_ownership_pct',
    table: 'k1_partners_tax_years',
    column: 'ownership_pct',
    label: 'Ownership',
    type: 'percent',
    validate: 'percent',
    yearly: true,
  },
  {
    key: 'owner_profit_allocation_pct',
    table: 'k1_partners_tax_years',
    column: 'profit_allocation_pct',
    label: 'Profit share',
    type: 'percent',
    validate: 'percent',
    yearly: true,
  },
  {
    key: 'owner_capital_pct',
    table: 'k1_partners_tax_years',
    column: 'capital_pct',
    label: 'Capital share',
    type: 'percent',
    validate: 'percent',
    yearly: true,
  },
  {
    key: 'owner_beginning_capital_account',
    table: 'k1_partners_tax_years',
    column: 'beginning_capital_account',
    label: 'Beginning capital account',
    type: 'money',
    // 'money' allows a negative amount, which a capital account regularly is.
    validate: 'money',
    yearly: true,
  },

  // entity_officers
  {
    key: 'officer_full_legal_name',
    table: 'entity_officers',
    column: 'full_legal_name',
    label: 'Name',
    type: 'text',
    required: true,
  },
  {
    key: 'officer_ssn',
    table: 'entity_officers',
    column: 'ssn_encrypted',
    label: 'Taxpayer no.',
    type: 'tin',
    validate: 'tin',
    encrypted: true,
    hashColumn: 'ssn_hash',
  },
  {
    key: 'officer_title',
    table: 'entity_officers',
    column: 'title',
    label: 'Title',
    type: 'text',
  },
  {
    key: 'officer_is_signing_officer',
    table: 'entity_officers',
    column: 'is_signing_officer',
    label: 'Signs the return',
    type: 'boolean',
    validate: 'boolean',
    required: true,
  },
  {
    key: 'officer_time_devoted_pct',
    table: 'entity_officers',
    column: 'time_devoted_pct',
    label: 'Time devoted',
    type: 'percent',
    validate: 'percent',
  },
  {
    key: 'officer_w2_compensation',
    table: 'entity_officers',
    column: 'w2_compensation',
    label: 'Compensation',
    type: 'money',
    validate: 'amount',
    taxYearColumn: 'w2_compensation_tax_year',
  },

  // entity_subsidiaries
  {
    key: 'subsidiary_name',
    table: 'entity_subsidiaries',
    column: 'name',
    label: 'Name',
    type: 'text',
  },
  {
    key: 'subsidiary_ein',
    table: 'entity_subsidiaries',
    column: 'ein',
    label: 'EIN',
    type: 'ein',
    validate: 'ein',
  },
  {
    key: 'subsidiary_ownership_pct',
    table: 'entity_subsidiaries',
    column: 'ownership_pct',
    label: 'Ownership',
    type: 'percent',
    validate: 'percent',
  },
  {
    key: 'subsidiary_entity_type',
    table: 'entity_subsidiaries',
    column: 'entity_type',
    label: 'Entity type',
    type: 'choice',
    choices: choices(ENTITY_TYPE_VALUES, ENTITY_TYPE_LABELS),
  },

  // entity_state_tax_ids
  {
    key: 'state_registration_state',
    table: 'entity_state_tax_ids',
    column: 'state',
    label: 'State',
    type: 'state',
    validate: 'state',
    required: true,
  },
  {
    key: 'state_registration_state_tax_id',
    table: 'entity_state_tax_ids',
    column: 'state_tax_id',
    label: 'State tax ID',
    type: 'text',
    required: true,
  },
  {
    key: 'state_registration_tax_type',
    table: 'entity_state_tax_ids',
    column: 'tax_type',
    label: 'Tax type',
    type: 'choice',
    choices: choices(STATE_TAX_TYPE_VALUES, STATE_TAX_TYPE_LABELS),
  },
]);

/** One entry of TAX_PROFILE_LIST_COLUMNS, its key one of the keys the list has. */
export type TaxProfileListColumnDefinition =
  (typeof TAX_PROFILE_LIST_COLUMNS)[number];
export type TaxProfileListColumnKey = TaxProfileListColumnDefinition['key'];

/**
 * The key of anything the page shows and writes as one value: a field of TAX_PROFILE_FIELDS, or a
 * column of TAX_PROFILE_LIST_COLUMNS on one row of its table (the write then also names the row by
 * recordId). The field routes take either; fieldWrites.ts looks the key up in both lists.
 */
export type TaxProfileWritableKey =
  | TaxProfileFieldKey
  | TaxProfileListColumnKey;

/** The list-table column for a key, or undefined for a key that is not one. */
export const taxProfileListColumnByKey = (
  key: string
): TaxProfileListColumnDefinition | undefined =>
  TAX_PROFILE_LIST_COLUMNS.find((column) => column.key === key);

const LIST_COLUMN_BY_TABLE_AND_COLUMN = new Map<
  string,
  TaxProfileListColumnDefinition
>(
  TAX_PROFILE_LIST_COLUMNS.map((definition) => [
    `${definition.table}|${definition.column}`,
    definition,
  ])
);

/**
 * The list-table column for a table and a column of it, or undefined when TAX_PROFILE_LIST_COLUMNS
 * has none: how a row of tax_profile_field_sources, which names a value by table_name and
 * field_name, finds the list column it is about.
 */
export const taxProfileListColumnByColumn = (
  table: string,
  column: string
): TaxProfileListColumnDefinition | undefined =>
  LIST_COLUMN_BY_TABLE_AND_COLUMN.get(`${table}|${column}`);

/**
 * The four lists of the profile page: the list tables each list's columns live on, and the key of
 * the list column that says who or what a row of the list is (a name, or for a state registration
 * its state). A row whose column under that key has no approved value is new
 * (TaxProfileListRow.isNew), and the document summary screen names a row by that column's value.
 */
export const TAX_PROFILE_LISTS: Readonly<
  Record<
    TaxProfileListName,
    {
      tables: ReadonlyArray<TaxProfileListTable>;
      nameKey: TaxProfileListColumnKey;
    }
  >
> = {
  owners: {
    tables: ['k1_partners', 'k1_partners_tax_years'],
    nameKey: 'owner_full_legal_name',
  },
  officers: { tables: ['entity_officers'], nameKey: 'officer_full_legal_name' },
  subsidiaries: { tables: ['entity_subsidiaries'], nameKey: 'subsidiary_name' },
  stateTaxIds: {
    tables: ['entity_state_tax_ids'],
    nameKey: 'state_registration_state',
  },
};

/** The list of the profile page a list table's rows are shown in (TAX_PROFILE_LISTS). */
export const taxProfileListOfTable = (
  table: TaxProfileListTable
): TaxProfileListName =>
  (Object.keys(TAX_PROFILE_LISTS) as TaxProfileListName[]).find((list) =>
    TAX_PROFILE_LISTS[list].tables.includes(table)
  ) as TaxProfileListName;
